import React, { useMemo } from "react";

const PAGE_SIZES = [50, 100, 200];

/**
 * Auto Sourcing results pagination — Storefront-style sticky footer with a
 * page-size select and numbered page buttons (1, 2, 3, 4 …) + next.
 */
export default function AutoSourcingPagination({
  page = 1,
  pageSize = 12,
  total = 0,
  onPageChange,
  onPageSizeChange,
}) {
  const totalNum = Math.max(0, Number(total) || 0);
  const size = Math.max(1, Number(pageSize) || 12);
  const totalPages = Math.max(1, Math.ceil(totalNum / size) || 1);
  const current = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const from = totalNum === 0 ? 0 : (current - 1) * size + 1;
  const to = Math.min(current * size, totalNum);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    // Window of up to 4 numbered pages around the current page.
    let start = Math.max(1, current - 1);
    let end = Math.min(totalPages, start + 3);
    start = Math.max(1, end - 3);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [current, totalPages]);

  if (totalNum <= 0) return null;

  return (
    <nav
      className="as-pagination as-pagination-sticky"
      data-testid="as-pagination"
      aria-label="Auto Sourcing results pages"
    >
      <div className="as-pagination-left">
        <label className="as-pagination-size" data-testid="as-pagination-size">
          <span>Products</span>
          <select
            value={size}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            aria-label="Products per page"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <span className="as-pagination-count" data-testid="as-pagination-range">
          {from}–{to} of {totalNum.toLocaleString()} products
        </span>
      </div>

      <div className="as-pagination-controls">
        <button
          type="button"
          className="as-page-btn"
          disabled={current <= 1}
          onClick={() => onPageChange?.(1)}
          aria-label="First page"
          data-testid="as-pagination-first"
        >
          |‹
        </button>
        <button
          type="button"
          className="as-page-btn"
          disabled={current <= 1}
          onClick={() => onPageChange?.(current - 1)}
          aria-label="Previous page"
          data-testid="as-pagination-prev"
        >
          ‹
        </button>
        {pageNumbers.map((n) => (
          <button
            key={n}
            type="button"
            className={n === current ? "as-page-pill" : "as-page-btn as-page-num"}
            aria-current={n === current ? "page" : undefined}
            aria-label={`Page ${n}`}
            data-testid={`as-pagination-page-${n}`}
            onClick={() => onPageChange?.(n)}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          className="as-page-btn"
          disabled={current >= totalPages}
          onClick={() => onPageChange?.(current + 1)}
          aria-label="Next page"
          data-testid="as-pagination-next"
        >
          ›
        </button>
        <button
          type="button"
          className="as-page-btn"
          disabled={current >= totalPages}
          onClick={() => onPageChange?.(totalPages)}
          aria-label="Last page"
          data-testid="as-pagination-last"
        >
          ›|
        </button>
        <span className="as-page-of" data-testid="as-pagination-page-label">
          Page {current} of {totalPages}
        </span>
      </div>
    </nav>
  );
}
